import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Tag, Clock } from "lucide-react";

import AppShell from "@/components/AppShell";
import { MAX_BULK, parseTagList } from "@/lib/cameraUtils";
import { p50IdentifyMs, computeBulkEta, formatEtaMs } from "@/lib/identifyEta";
import { BULK_CONCURRENCY } from "@/hooks/useBulkIdentify";

/**
 * BulkQueueView — the "identifying N photos…" screen shown while a bulk
 * batch is in flight or immediately after it finishes.
 *
 * Extracted from Camera.jsx (iter62). Purely presentational — every mutation
 * flows through the `bulk.actions` object handed in from the parent's
 * `useBulkIdentify()` hook so the source of truth stays with the hook.
 */
export default function BulkQueueView({ bulk }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    bulkQueue, bulkRunning, bulkDone, bulkStartedAt, trimmed, pendingBatch,
    bulkTag, bulkTagApplied, knownTags,
  } = bulk.state;

  const total = bulkQueue.length;
  const completed = bulkQueue.filter(
    (q) => q.status === "done" || q.status === "error" || q.status === "queued"
  ).length;

  // Compute elapsed + smoothed ETA. Only meaningful while the batch is still
  // processing (once done, we show the final elapsed time only).
  const etaBaseline = p50IdentifyMs();
  const eta = bulkStartedAt
    ? computeBulkEta(bulkQueue, BULK_CONCURRENCY, etaBaseline, bulkStartedAt)
    : { elapsedMs: 0, remainingMs: 0 };
  const elapsedLabel = formatEtaMs(eta.elapsedMs);
  // Never say "0s remaining" or a negative — once we blow past the estimate,
  // transition into the soothing "almost there…" copy.
  const showAlmostThere = bulkRunning && completed < total && eta.remainingMs < 3_000;
  const remainingLabel = bulkRunning && !showAlmostThere && completed < total
    ? formatEtaMs(eta.remainingMs)
    : null;

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto">
        <div className="px-5 pt-2">
          <h1 className="font-display text-3xl text-leaf-ink leading-tight">
            {bulkRunning ? t("camera.bulkProcessing") : t("camera.bulkDone")}
          </h1>
          <p className="text-sm text-leaf-mist mt-1">
            {t("camera.bulkProgress", { done: completed, total })}
          </p>
        </div>

        <div className="px-5 mt-4">
          <div className="h-1.5 rounded-full bg-parchment overflow-hidden" data-testid="bulk-progress">
            <div
              className="h-full bg-leaf transition-all duration-300"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
          {/* Elapsed + ETA — presentational only. Doesn't change wall time; helps
              the wait feel intentional during the slow ~20-30s Gemini round-trip.
              Hidden after the batch finishes. */}
          {bulkRunning && (
            <div
              data-testid="bulk-eta"
              className="mt-2 flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-leaf-mist"
              aria-live="polite"
            >
              <span data-testid="bulk-eta-elapsed" className="inline-flex items-center gap-1.5">
                <Clock className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                {t("camera.bulkEtaElapsed", { time: elapsedLabel })}
              </span>
              {showAlmostThere ? (
                <span data-testid="bulk-eta-almost">{t("camera.bulkEtaAlmostThere")}</span>
              ) : remainingLabel ? (
                <span data-testid="bulk-eta-remaining">
                  {t("camera.bulkEtaRemaining", { time: remainingLabel })}
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* Bulk-tag input — apply the same tag(s) to every scan in this batch.
            Persists across "Continue with next N" so repeated batches share it. */}
        <div className="px-5 mt-4">
          <label
            htmlFor="bulk-tag-input"
            className="block text-[11px] uppercase tracking-[0.16em] text-leaf-mist mb-1.5"
          >
            {t("camera.bulkTagLabel")}
          </label>
          {knownTags.length > 0 && (
            <div className="mb-2" data-testid="bulk-tag-suggestions">
              <p className="text-[10px] uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("camera.bulkTagSuggestionsLabel")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {knownTags.slice(0, 12).map((tg) => {
                  const active = parseTagList(bulkTag).some(
                    (x) => x.toLowerCase() === tg.toLowerCase()
                  );
                  return (
                    <button
                      key={tg}
                      type="button"
                      onClick={() => bulk.actions.appendBulkTag(tg)}
                      disabled={active}
                      aria-pressed={active}
                      data-testid={`bulk-tag-suggestion-${tg.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border transition ${
                        active
                          ? "bg-leaf text-white border-leaf cursor-default"
                          : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
                      }`}
                    >
                      <Tag className="w-3 h-3" strokeWidth={1.5} /> {tg}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="relative">
            <Tag
              className="w-4 h-4 text-leaf-mist absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              strokeWidth={1.5}
            />
            <input
              id="bulk-tag-input"
              data-testid="bulk-tag-input"
              type="text"
              value={bulkTag}
              onChange={(e) => {
                bulk.actions.setBulkTag(e.target.value);
                bulk.actions.setBulkTagApplied(false);
              }}
              placeholder={t("camera.bulkTagPlaceholder")}
              className="w-full bg-white border border-[#E8E5DF] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/30 placeholder:text-leaf-mist/70"
              maxLength={120}
            />
          </div>
          <p className="text-[11px] text-leaf-mist mt-1.5">
            {bulkTagApplied
              ? t("camera.bulkTagAppliedHint")
              : parseTagList(bulkTag).length > 0
                ? t("camera.bulkTagQueuedHint", { count: parseTagList(bulkTag).length })
                : t("camera.bulkTagHint")}
          </p>
        </div>

        {trimmed.length > 0 && (
          <div
            className="mx-5 mt-4 bg-[#FFF4DC] border border-[#E9C46A]/40 rounded-2xl p-3 text-sm"
            data-testid="trimmed-notice"
          >
            <p className="text-[#8a6a14] font-medium">
              {trimmed.length === 1
                ? t("camera.trimmedNoticeOne", { n: trimmed.length, max: MAX_BULK })
                : t("camera.trimmedNoticeMany", { n: trimmed.length, max: MAX_BULK })}
            </p>
            <ul className="mt-1.5 text-[12px] text-[#8a6a14]/90 list-disc pl-5">
              {trimmed.slice(0, 5).map((n, i) => (
                <li key={`${n}-${i}`} className="truncate">{n}</li>
              ))}
              {trimmed.length > 5 && <li>{t("camera.trimmedMore", { n: trimmed.length - 5 })}</li>}
            </ul>
          </div>
        )}

        <ul
          className="mx-5 mt-5 space-y-2"
          data-testid="bulk-queue"
          aria-live="polite"
          aria-label={t("camera.bulkQueueAria")}
        >
          {bulkQueue.map((item, i) => (
            <li
              key={item.qid || `${item.name}-${i}`}
              data-testid={`bulk-item-${i}`}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-2.5 flex items-center gap-3"
            >
              <div className="w-12 h-12 rounded-xl bg-parchment overflow-hidden shrink-0">
                {item.thumb && <img src={item.thumb} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-leaf-ink truncate font-medium">{item.name}</p>
                <p className="text-[12px] text-leaf-mist" data-testid={`bulk-item-${i}-status`}>
                  {item.status === "pending" && t("camera.queued")}
                  {item.status === "processing" && t("camera.identifyingItem")}
                  {item.status === "done" && t("camera.saved")}
                  {item.status === "queued" && t("camera.savedNeedsId")}
                  {item.status === "error" && (item.error || t("camera.failed"))}
                </p>
              </div>
              <div className="shrink-0">
                {item.status === "pending" && <span className="text-leaf-mist text-xs">●</span>}
                {item.status === "processing" && (
                  <Loader2 className="w-5 h-5 text-leaf animate-spin" strokeWidth={1.5} />
                )}
                {item.status === "done" && <CheckCircle2 className="w-5 h-5 text-leaf" strokeWidth={1.5} />}
                {item.status === "queued" && (
                  <Clock
                    className="w-5 h-5 text-amber-600"
                    strokeWidth={1.5}
                    aria-label={t("camera.savedNeedsId")}
                  />
                )}
                {item.status === "error" && <AlertCircle className="w-5 h-5 text-rust" strokeWidth={1.5} />}
              </div>
            </li>
          ))}
        </ul>

        {bulkRunning && (
          <div className="px-5 mt-5">
            <button
              onClick={bulk.actions.cancel}
              data-testid="bulk-cancel"
              className="w-full rounded-full py-3 bg-parchment text-rust font-medium hover:bg-[#EEE9DE] transition active:scale-[0.98]"
            >
              {t("camera.cancelRemaining")}
            </button>
          </div>
        )}

        {bulkDone && (
          <div className="px-5 mt-6 flex flex-col gap-3">
            {pendingBatch.length > 0 && (
              <button
                onClick={bulk.actions.continueNext}
                data-testid="bulk-continue-next"
                className="w-full rounded-full py-3.5 bg-leaf text-white font-medium hover:bg-leaf-deep transition active:scale-[0.98] shadow-botanical"
              >
                {pendingBatch.length <= MAX_BULK
                  ? t("camera.continueBatchAll", { n: pendingBatch.length })
                  : t("camera.continueBatch", { n: Math.min(MAX_BULK, pendingBatch.length) })}
              </button>
            )}
            <div className="flex gap-3">
              <button
                onClick={bulk.actions.reset}
                data-testid="bulk-back-camera"
                className="flex-1 rounded-full py-3.5 bg-parchment text-leaf-ink font-medium hover:bg-[#EEE9DE] transition active:scale-[0.98]"
              >
                {t("camera.addMore")}
              </button>
              <button
                onClick={() => navigate("/gallery")}
                data-testid="bulk-view-gallery"
                className="flex-[1.4] rounded-full py-3.5 bg-leaf text-white font-medium hover:bg-leaf-deep transition active:scale-[0.98] shadow-botanical"
              >
                {t("camera.viewMyPlants")}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
